/**
 * Modal instance management utilities
 */

/**
 * Window interface extension for active modal instance tracking
 */
declare global {
  interface Window {
    __3DS_MODAL_ACTIVE_INSTANCE__?: ThreeDSModalInstance;
  }
}

export interface ThreeDSModalInstance {
  readonly instanceId: string;
  close: (result?: { success: boolean; error?: string }) => void;
}

/**
 * Cleans up any existing active modal instance
 */
export function cleanupPreviousModal(
  currentInstance: ThreeDSModalInstance,
  log?: (message: string, ...args: unknown[]) => void
): void {
  if (typeof window === 'undefined') return;

  const previousModal = window.__3DS_MODAL_ACTIVE_INSTANCE__;
  if (previousModal && previousModal !== currentInstance) {
    log?.(`Cleaning up previous modal instance: ${previousModal.instanceId}`);
    previousModal.close({ success: false, error: 'Replaced by new modal instance' });
  }
}

/**
 * Sets a modal instance as the active one
 */
export function setAsActiveModal(
  instance: ThreeDSModalInstance,
  setIsActive?: (value: boolean) => void,
  log?: (message: string, ...args: unknown[]) => void
): void {
  if (typeof window === 'undefined') return;
  window.__3DS_MODAL_ACTIVE_INSTANCE__ = instance;
  setIsActive?.(true);
  log?.(`Set as active modal instance: ${instance.instanceId}`);
}

/**
 * Clears a modal instance as active if it's currently active
 */
export function clearActiveModal(
  instance: ThreeDSModalInstance,
  setIsActive?: (value: boolean) => void,
  log?: (message: string, ...args: unknown[]) => void
): void {
  if (typeof window === 'undefined') return;
  if (window.__3DS_MODAL_ACTIVE_INSTANCE__ === instance) {
    delete window.__3DS_MODAL_ACTIVE_INSTANCE__;
    setIsActive?.(false);
    log?.(`Cleared active modal instance: ${instance.instanceId}`);
  }
}

/**
 * Checks if a modal instance is still the active one
 */
export function isModalActive(instance: ThreeDSModalInstance): boolean {
  if (typeof window === 'undefined') return true;
  return window.__3DS_MODAL_ACTIVE_INSTANCE__ === instance;
}
